/**
 * Cards — every photograph in the archive, narrowed by the filter.
 *
 * Virtualised, because a family archive is tens of thousands of scans and a DOM
 * node per scan is a tab that stops responding. The scroller is a spacer of the
 * full height; only the rows crossing the viewport carry tiles. Paging is by
 * `nextCursor`, fetched when the rendered window comes within a couple of rows
 * of the end of what is loaded.
 *
 * Album detail is this view with `albumId` set on the shared filter — there is
 * no second grid.
 */

import { h, replace, clear, patch } from "../dom";
import { api } from "../api";
import { store } from "../state";
import { empty, failure, loading, tile, toast } from "../ui";
import { count } from "../format";
import { openAsset } from "../lightbox";
import type { AssetCard, AlbumSummary } from "../types";
import type { RefreshOptions, View } from "./view";

const PAGE = 60;

/**
 * The grid's metrics, from the stylesheet.
 *
 * These were constants here and rules there, which agreed until a phone
 * breakpoint changed the tile height: the spacer kept reserving 196px a row
 * for rows that were now 172px, so the bottom of the archive was a screen of
 * empty scroll and `first`/`last` addressed the wrong rows on the way down.
 * Reading them means one definition, and a breakpoint can change the shape of
 * the grid without touching this file.
 */
interface Metrics {
  gap: number;
  minCol: number;
  rowH: number;
}

function metricsOf(grid: HTMLElement): Metrics {
  const cs = getComputedStyle(grid);
  const px = (name: string, fallback: number): number => {
    const v = parseFloat(cs.getPropertyValue(name));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  };
  const gap = px("--grid-gap", 12);
  return { gap, minCol: px("--tile-min", 152), rowH: px("--tile-h", 184) + gap };
}

function assetRatio(asset: AssetCard): number {
  if (!asset.width || !asset.height) return 4 / 3;
  const rotated = asset.orientation >= 5 && asset.orientation <= 8;
  return rotated ? asset.height / asset.width : asset.width / asset.height;
}

export function mount(pane: HTMLElement): View {
  const head = h("div", { class: "gridhead" });
  const grid = h("div", { class: "vgrid" });
  const win = h("div", { class: "win" });
  const foot = h("div", { class: "gridfoot" });
  grid.appendChild(win);
  replace(pane, head, grid, foot);

  let items: AssetCard[] = [];
  let cursor: string | null = null;
  let total: number | null = null;
  let loadingMore = false;
  let exhausted = false;
  let cols = 1;
  let generation = 0;
  let measuredWidth = 0;

  let m: Metrics = { gap: 12, minCol: 152, rowH: 196 };

  const measure = (): void => {
    m = metricsOf(grid);
    const w = grid.clientWidth;
    measuredWidth = w;
    cols = Math.max(1, Math.floor((w + m.gap) / (m.minCol + m.gap)));
  };

  const paint = (): void => {
    measure();
    grid.className = `vgrid ${store.cardLayout}`;
    if (store.cardLayout !== "gallery") {
      grid.style.removeProperty("height");
      win.style.removeProperty("top");
      win.style.removeProperty("grid-template-columns");

      if (store.cardLayout === "masonry") {
        win.style.gridTemplateColumns = `repeat(${cols},minmax(0,1fr))`;
        const columns = Array.from({ length: cols }, () => h("div", { class: "masonry-column" }));
        const heights = Array<number>(cols).fill(0);
        const colWidth = Math.max(1, (grid.clientWidth - m.gap * (cols - 1)) / cols);
        for (const asset of items) {
          const ratio = assetRatio(asset);
          const columnIndex = heights.indexOf(Math.min(...heights));
          const card = tile(asset, openAsset);
          card.style.setProperty("--asset-ratio", String(ratio));
          columns[columnIndex]!.appendChild(card);
          heights[columnIndex] = heights[columnIndex]! + colWidth / ratio + 52 + m.gap;
        }
        patch(win, columns);
      } else {
        patch(win, items.map((asset) => tile(asset, openAsset)));
      }
      if (!exhausted && pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 600) void more();
      return;
    }

    const rows = Math.ceil(items.length / cols);
    grid.style.height = rows ? `${rows * m.rowH - m.gap}px` : "0px";
    win.style.gridTemplateColumns = `repeat(${cols},minmax(0,1fr))`;

    const top = pane.scrollTop - grid.offsetTop;
    const first = Math.max(0, Math.floor(top / m.rowH) - 2);
    const last = Math.min(rows - 1, Math.floor((top + pane.clientHeight) / m.rowH) + 2);
    win.style.top = `${first * m.rowH}px`;

    // Patched rather than cleared: scrolling repaints on every frame, and a
    // tile that is still on screen should keep its decoded image.
    patch(
      win,
      rows > 0 ? items.slice(first * cols, (last + 1) * cols).map((a) => tile(a, openAsset)) : [],
    );
    // Fetch when the painted window is within two rows of the loaded end, and
    // also when everything loaded so far does not even fill the pane.
    if (!exhausted && (last >= rows - 3 || grid.clientHeight < pane.clientHeight)) {
      void more();
    }
  };

  const setFoot = (): void => {
    if (loadingMore) replace(foot, loading("more"));
    else if (exhausted && items.length > PAGE)
      replace(foot, h("div", { class: "loading", text: `— all ${items.length} —` }));
    else clear(foot);
  };

  async function more(): Promise<void> {
    if (loadingMore || exhausted) return;
    loadingMore = true;
    setFoot();
    const mine = generation;
    try {
      const slice = await api.cards(store.libraryId, store.filter, {
        limit: PAGE,
        cursor: cursor ?? undefined,
      });
      if (mine !== generation) return;
      items = items.concat(slice.items);
      cursor = slice.nextCursor;
      total = slice.total;
      exhausted = !slice.nextCursor;
      loadingMore = false;
      renderHead();
      paint();
      setFoot();
    } catch (e) {
      if (mine !== generation) return;
      loadingMore = false;
      exhausted = true;
      setFoot();
      if (!items.length) replace(pane, failure(e, refresh));
      else toast(e instanceof Error ? e.message : "could not load more", true);
    }
  }

  let album: AlbumSummary | null = null;

  function renderHead(): void {
    renderHeadInto(head);
  }

  function renderHeadInto(target: HTMLElement): void {
    const bits: Array<Node | null> = [];
    if (store.filter.albumId) {
      bits.push(
        h(
          "div",
          { class: "albumhead" },
          h("button", {
            class: "btn sm ghost",
            text: "← All albums",
            onclick: () => {
              store.setFilter({ albumId: undefined });
              store.go("albums");
            },
          }),
          h("h2", { text: album?.name ?? "Album" }),
          album?.description ? h("p", { class: "sub", text: album.description }) : null,
        ),
      );
    }
    const n = total ?? items.length;
    bits.push(
      h(
        "div",
        { class: "gridcount mono" },
        `${count(n, "photograph")}${store.narrowed ? " match" : ""}`,
      ),
    );
    replace(target, ...bits.filter(Boolean));
  }

  function emptyState(): HTMLElement {
    if (store.narrowed) {
      return empty({
        icon: "search",
        title: "Nothing matches that",
        body: [
          "The archive is not empty — this narrowing is. Widen the date range, drop a person, or clear the filter and start again.",
        ],
        actions: [
          h("button", {
            class: "btn primary",
            text: "Clear the filter",
            onclick: () => store.clearFilter(),
          }),
        ],
      });
    }
    return empty({
      icon: "upload",
      title: "This library has nothing in it yet",
      body: [
        "Point the desktop app at a folder of scans and it will hash, date and index them here. Photographs appear in this grid as they arrive — you do not need to reload.",
      ],
      actions: [],
    });
  }

  /**
   * The same photographs, re-read, with only the changed tiles redrawn.
   *
   * Re-reads as much as is already loaded rather than the first page, so a
   * reader four hundred photographs down stays four hundred photographs down.
   */
  async function update(): Promise<void> {
    const mine = ++generation;
    const want = Math.max(PAGE, items.length);
    const fresh: AssetCard[] = [];
    let next: string | null = null;
    let freshTotal: number | null = total;
    try {
      do {
        const slice = await api.cards(store.libraryId, store.filter, {
          limit: Math.min(200, want - fresh.length),
          cursor: next ?? undefined,
        });
        if (mine !== generation) return;
        fresh.push(...slice.items);
        next = slice.nextCursor;
        freshTotal = slice.total;
      } while (next && fresh.length < want);
    } catch {
      // A live update that fails leaves the grid as it was. The next message
      // will try again, and an error panel over a working grid is worse.
      if (mine === generation) loadingMore = false;
      return;
    }
    if (!fresh.length) {
      replace(pane, emptyState());
      items = [];
      return;
    }
    items = fresh;
    cursor = next;
    total = freshTotal;
    exhausted = !next;
    loadingMore = false;
    const before = head.textContent;
    const scratch = head.cloneNode(false) as HTMLElement;
    renderHeadInto(scratch);
    if (scratch.textContent !== before) patch(head, Array.from(scratch.childNodes));
    paint();
    setFoot();
  }

  async function refresh(opts: RefreshOptions = {}): Promise<void> {
    // Only a grid that is showing something can be updated in place. An empty
    // state or a first load has nothing to keep.
    if (opts.quiet && items.length && grid.isConnected) return update();
    generation++;
    items = [];
    cursor = null;
    total = null;
    exhausted = false;
    loadingMore = false;
    album = null;
    const mine = generation;

    replace(pane, head, grid, foot);
    replace(head, loading("photographs"));
    clear(win);
    grid.style.height = "0px";

    if (store.filter.albumId) {
      try {
        const all = await api.albums(store.libraryId);
        album = all.find((a) => a.id === store.filter.albumId) ?? null;
      } catch {
        album = null;
      }
      if (mine !== generation) return;
    }

    await more();
    if (mine !== generation) return;
    if (!items.length) {
      replace(pane, emptyState());
      return;
    }
  }

  const onScroll = (): void => {
    if (!items.length) return;
    if (store.cardLayout === "gallery") paint();
    else if (!exhausted && pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 600) void more();
  };
  pane.addEventListener("scroll", onScroll, { passive: true });

  const onResize = (): void => {
    const before = cols;
    const beforeWidth = measuredWidth;
    measure();
    if ((before !== cols || beforeWidth !== measuredWidth) && items.length) paint();
  };
  window.addEventListener("resize", onResize, { passive: true });

  const ro = new ResizeObserver(onResize);
  ro.observe(grid);

  measure();
  void refresh();

  return {
    refresh: (opts) => void refresh(opts),
    destroy() {
      generation++;
      pane.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    },
  };
}
